import React, { useEffect, useState } from "react";
import { Plus, Trash2, Phone, Mail, MapPin, GraduationCap, UserCheck, Search } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { FileUploader } from "../FileUploader";

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

// ---------------- School Contacts Panel ----------------
export const SchoolContactsPanel = ({ readOnly = false } = {}) => {
    const [items, setItems] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ name: "", role: "", school: "", phone: "", email: "", notes: "" });
    const [q, setQ] = useState("");

    const load = async () => {
        try { const { data } = await api.get("/admin/school-contacts"); setItems(data || []); }
        catch { toast.error("Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.name || !form.role) { toast.error("Name and role required"); return; }
        try {
            await api.post("/admin/school-contacts", { ...form, email: form.email || null });
            toast.success("Contact saved");
            setAdding(false);
            setForm({ name: "", role: "", school: "", phone: "", email: "", notes: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete contact?")) return;
        try { await api.delete(`/admin/school-contacts/${id}`); load(); } catch {}
    };

    const filtered = q
        ? items.filter((c) => `${c.name} ${c.role} ${c.school || ""} ${c.email || ""}`.toLowerCase().includes(q.toLowerCase()))
        : items;

    return (
        <div data-testid="admin-school-contacts-panel" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">School & community contacts</h2>
                    <p className="text-xs text-[#4A5568] mt-1">Private directory of principals, PTA chairs, custodians, and other community partners.</p>
                </div>
                <button onClick={() => setAdding(true)} data-testid="contact-new-btn" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${readOnly ? "hidden" : "bg-[#E53935] hover:bg-[#D32F2F] text-white"}`}>
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add contact
                </button>
            </div>

            <div className="relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, role, school…"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-black/10 text-sm"
                    data-testid="contact-search"
                />
            </div>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contact-list">
                {filtered.length === 0 && <li className="col-span-2 text-sm text-[#4A5568] py-10 text-center">{items.length === 0 ? "No contacts yet." : "No matches."}</li>}
                {filtered.map((c) => (
                    <li key={c.id} data-testid={`contact-row-${c.id}`} className="bg-white border border-black/5 rounded-2xl p-5">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                                <h3 className="font-heading text-lg text-[#1C252E] font-medium">{c.name}</h3>
                                <p className="text-xs uppercase tracking-wider text-[#E53935] font-bold mt-1 inline-flex items-center gap-1">
                                    <UserCheck className="w-3 h-3" /> {c.role}
                                </p>
                            </div>
                            <button onClick={() => del(c.id)} className={`p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA] ${readOnly ? "hidden" : ""}`} aria-label="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                        </div>
                        <dl className="text-xs text-[#4A5568] space-y-1 mt-2">
                            {c.school && <p className="inline-flex items-center gap-1.5"><GraduationCap className="w-3 h-3" /> {c.school}</p>}
                            {c.phone && <p className="inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> {c.phone}</p>}
                            {c.email && <p className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> {c.email}</p>}
                            {c.notes && <p className="mt-2 text-[11px] italic leading-relaxed">{c.notes}</p>}
                        </dl>
                    </li>
                ))}
            </ul>

            {adding && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setAdding(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-xl w-full my-8 p-6">
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-4">Add school/community contact</h3>
                        <div className="space-y-3">
                            <input placeholder="Name (e.g. Jane Smith)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="Role (Principal, PTA Chair, Custodian)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="contact-form-role" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="School / org" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <textarea placeholder="Internal notes (preferences, best contact times, history)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-xl border border-black/10 px-4 py-3" />
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-5">
                            <button onClick={() => setAdding(false)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                            <button onClick={save} data-testid="contact-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">Save contact</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


// ---------------- Expert Opinions Panel ----------------
export const ExpertOpinionsPanel = ({ readOnly = false } = {}) => {
    const [items, setItems] = useState([]);
    const [requests, setRequests] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ spend_request_id: "", sme_name: "", sme_credentials: "", opinion: "", recommendation: "advise", document_url: "" });

    const load = async () => {
        try {
            const [o, r] = await Promise.all([
                api.get("/admin/expert-opinions"),
                api.get("/admin/spend-requests"),
            ]);
            setItems(o.data || []);
            setRequests(r.data || []);
        } catch { toast.error("Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.sme_name || !form.sme_credentials || !form.opinion) {
            toast.error("Name, credentials, and opinion required");
            return;
        }
        try {
            await api.post("/admin/expert-opinions", { ...form, spend_request_id: form.spend_request_id || null });
            toast.success("Opinion filed");
            setAdding(false);
            setForm({ spend_request_id: "", sme_name: "", sme_credentials: "", opinion: "", recommendation: "advise", document_url: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete opinion?")) return;
        try { await api.delete(`/admin/expert-opinions/${id}`); load(); } catch {}
    };

    const recBadge = (r) => ({
        approve: "bg-green-100 text-green-700",
        reject: "bg-red-100 text-red-700",
        advise: "bg-blue-100 text-blue-700",
    }[r] || "bg-gray-100 text-gray-600");

    return (
        <div data-testid="admin-expert-opinions-panel" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Expert sanity-check opinions</h2>
                    <p className="text-xs text-[#4A5568] mt-1">Capture independent reviews from subject-matter experts (e.g. retired contractors, engineers, educators) — attach to a spend request or keep as a standalone reference.</p>
                </div>
                <button onClick={() => setAdding(true)} data-testid="opinion-new-btn" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${readOnly ? "hidden" : "bg-[#E53935] hover:bg-[#D32F2F] text-white"}`}>
                    <Plus className="w-4 h-4" strokeWidth={2} /> File opinion
                </button>
            </div>

            <ul className="space-y-3" data-testid="opinion-list">
                {items.length === 0 && <li className="text-sm text-[#4A5568] py-10 text-center">No expert opinions filed yet.</li>}
                {items.map((o) => {
                    const sr = o.spend_request_id ? requests.find((r) => r.id === o.spend_request_id) : null;
                    return (
                        <li key={o.id} data-testid={`opinion-row-${o.id}`} className="bg-white border border-black/5 rounded-2xl p-5">
                            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h3 className="font-heading text-lg text-[#1C252E] font-medium">{o.sme_name}</h3>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${recBadge(o.recommendation)}`}>
                                            {o.recommendation}
                                        </span>
                                    </div>
                                    <p className="text-xs text-[#4A5568] italic">{o.sme_credentials}</p>
                                    {sr && <p className="text-xs text-[#4A5568] mt-1">Re: <strong>{sr.title}</strong> · ${sr.amount.toLocaleString()}</p>}
                                    <p className="text-sm text-[#1C252E] mt-3 leading-relaxed whitespace-pre-wrap">{o.opinion}</p>
                                    {o.document_url && (
                                        <a href={o.document_url} target="_blank" rel="noreferrer" className="text-xs text-[#E53935] hover:underline mt-2 inline-block">View supporting document</a>
                                    )}
                                    <p className="text-[10px] text-[#4A5568] mt-2">Filed {fmtDate(o.created_at)} by {o.created_by}</p>
                                </div>
                                <button onClick={() => del(o.id)} className={`p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA] ${readOnly ? "hidden" : ""}`} aria-label="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {adding && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setAdding(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-8 p-6">
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-4">File an expert opinion</h3>
                        <div className="space-y-3">
                            <select value={form.spend_request_id} onChange={(e) => setForm({ ...form, spend_request_id: e.target.value })} data-testid="opinion-form-spend" className="w-full rounded-xl border border-black/10 px-4 py-3">
                                <option value="">Standalone (not tied to a spend request)</option>
                                {requests.filter((r) => r.status !== "paid" && r.status !== "rejected").map((r) => (
                                    <option key={r.id} value={r.id}>{r.title} — ${r.amount.toLocaleString()} ({r.status})</option>
                                ))}
                            </select>
                            <input placeholder="SME name" value={form.sme_name} onChange={(e) => setForm({ ...form, sme_name: e.target.value })} data-testid="opinion-form-sme-name" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="Credentials (e.g. 'Retired HVAC contractor, 30 years')" value={form.sme_credentials} onChange={(e) => setForm({ ...form, sme_credentials: e.target.value })} data-testid="opinion-form-creds" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <textarea placeholder="Opinion / sanity-check notes" value={form.opinion} onChange={(e) => setForm({ ...form, opinion: e.target.value })} rows={5} data-testid="opinion-form-text" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <select value={form.recommendation} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3">
                                <option value="approve">Recommend: APPROVE</option>
                                <option value="advise">Advise (no strong recommendation)</option>
                                <option value="reject">Recommend: REJECT</option>
                            </select>
                            <div>
                                <p className="text-xs text-[#4A5568] mb-1">Supporting document (optional)</p>
                                <FileUploader value={form.document_url} onChange={(url) => setForm({ ...form, document_url: url })} label="Upload PDF / image" testid="opinion-form-doc" />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-5">
                            <button onClick={() => setAdding(false)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                            <button onClick={save} data-testid="opinion-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">File opinion</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
